import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  Alert,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { attendanceWebApi } from './attendanceWebApi';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../../common/tableStyles';

const CARD_SX = {
  p: { xs: 2, md: 3 },
  borderRadius: '20px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};

function formatSeconds(secs) {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatTime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoStr;
  }
}

export default function WebAttendanceMonitor() {
  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Review modal state
  const [selectedReg, setSelectedReg] = useState(null);
  const [decision, setDecision] = useState('APPROVED');
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [successBanner, setSuccessBanner] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [monRes, revRes] = await Promise.all([
        attendanceWebApi.monitor(),
        attendanceWebApi.regularizationReviews(),
      ]);
      setSessions(monRes?.results || []);
      setReviews(revRes?.results || []);
    } catch (err) {
      console.error('Failed to load monitor data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openReviewModal = (item, dec) => {
    setSelectedReg(item);
    setDecision(dec);
    setReviewComment('');
    setReviewError('');
  };

  const submitReview = async () => {
    if (!reviewComment.trim()) {
      setReviewError('Please provide a comment for your decision.');
      return;
    }
    setReviewLoading(true);
    setReviewError('');
    try {
      await attendanceWebApi.reviewRegularization(selectedReg.id, {
        decision,
        comment: reviewComment.trim(),
      });
      setSuccessBanner(`Regularization request for ${selectedReg.employee_no} was ${decision.toLowerCase()}.`);
      setSelectedReg(null);
      loadData();
    } catch (err) {
      const data = err.response?.data;
      setReviewError(data?.code || data?.detail || err.message);
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <InfoOutlinedIcon color="primary" />
          <Typography variant="body2" color="text.secondary">
            <strong>Informational Evidence Only:</strong> Web attendance sessions and regularization reviews provide audit-logged records scoped to your branch. Official biometric records, leave balances, and payroll calculations remain completely separate.
          </Typography>
        </Stack>

        {successBanner && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessBanner('')}>
            {successBanner}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            mb: 2.5,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 48 },
            '& .Mui-selected': { color: '#2f6bff !important' },
            '& .MuiTabs-indicator': { bgcolor: '#2f6bff' },
          }}
        >
          <Tab icon={<VisibilityIcon />} iconPosition="start" label={`Live Branch Sessions (${sessions.length})`} />
          <Tab
            icon={<AssignmentTurnedInIcon />}
            iconPosition="start"
            label={`Regularization Requests (${reviews.filter((r) => r.status === 'PENDING').length} pending)`}
          />
        </Tabs>

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading data…
            </Typography>
          </Box>
        ) : activeTab === 0 ? (
          sessions.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No web attendance sessions found for your authorized branches.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={TABLE_PAPER_SX}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Date</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Clock In</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Clock Out</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Work Time</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Break</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Attribution</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((s) => {
                    const isClosed = Boolean(s.closed_at);
                    return (
                      <TableRow key={s.id} hover sx={TABLE_ROW_SX}>
                        <TableCell sx={BODY_CELL_SX}>
                          <Typography variant="body2" fontWeight={700}>
                            {s.employee_no}
                          </Typography>
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {s.attendance_date || formatDate(s.opened_at)}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>{formatTime(s.opened_at)}</TableCell>
                        <TableCell sx={BODY_CELL_SX}>{formatTime(s.closed_at)}</TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {formatSeconds(s.duration_excluding_breaks_seconds)}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {formatSeconds(s.completed_break_seconds)}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          <Chip
                            label={s.attribution_reason || 'UNRESOLVED'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {isClosed ? (
                            <Chip label="Completed" size="small" sx={{ bgcolor: '#F3F4F6', color: '#4B5563', fontWeight: 600 }} />
                          ) : s.active_break ? (
                            <Chip label="On Break" size="small" sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 700 }} />
                          ) : (
                            <Chip label="Active" size="small" sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 700 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : reviews.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No regularization requests found for your branch.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={TABLE_PAPER_SX}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Event</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Requested At</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Reason</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Comment</TableCell>
                  <TableCell sx={HEAD_CELL_SX} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviews.map((r) => {
                  const colorMap = {
                    PENDING: { bg: '#FEF3C7', fg: '#D97706' },
                    APPROVED: { bg: '#ECFDF5', fg: '#059669' },
                    REJECTED: { bg: '#FEE2E2', fg: '#DC2626' },
                  };
                  const c = colorMap[r.status] || { bg: '#F3F4F6', fg: '#6B7280' };
                  const isPending = r.status === 'PENDING';
                  return (
                    <TableRow key={r.id} hover sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" fontWeight={700}>
                          {r.employee_no}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip label={r.event_type.replace('_', ' ')} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {formatDate(r.requested_at)} {formatTime(r.requested_at)}
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={r.reason}>
                          {r.reason}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip label={r.status} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" color="text.secondary">
                          {r.review_comment || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX} align="center">
                        {isPending ? (
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => openReviewModal(r, 'APPROVED')}
                              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', minWidth: 68 }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => openReviewModal(r, 'REJECTED')}
                              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', minWidth: 68 }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Reviewed
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Decision Modal */}
      {selectedReg && (
        <Dialog open onClose={() => setSelectedReg(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {decision === 'APPROVED' ? 'Approve Regularization' : 'Reject Regularization'}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Recording decision for <strong>{selectedReg.employee_no}</strong> (
              {selectedReg.event_type.replace('_', ' ')} on {formatDate(selectedReg.requested_at)}).
            </Typography>
            {reviewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {reviewError}
              </Alert>
            )}
            <TextField
              label="Review Comment"
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              placeholder="e.g. Verified punch with branch manager logs."
              helperText="Review comment is saved with the audit record."
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSelectedReg(null)} disabled={reviewLoading} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color={decision === 'APPROVED' ? 'success' : 'error'}
              onClick={submitReview}
              disabled={reviewLoading}
              startIcon={reviewLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ fontWeight: 700 }}
            >
              {reviewLoading ? 'Submitting…' : `Confirm ${decision === 'APPROVED' ? 'Approval' : 'Rejection'}`}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

